import React, { SyntheticEvent, useEffect, useLayoutEffect } from 'react';
import { useCurrentProject } from '@material-bank/projects-utils';
import { getUserLang, isEuEnv } from '@mb/lib';
import { sendSearchAnalyticsEvent } from '@mb/lib/analytics';
import MBSearchFormV2 from 'components/Forms/MBSearchFormV2/MBSearchFormV2';
import UploadImageModal from 'components/Modals/UploadImageModal';
import { useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import * as Styled from './SearchFormContainer.styles';
import { searchActionsEnum } from '../../../../pages/ProductSearch/config/constants';
import {
  fetchSuggestionsByQuery,
  initAutocomplete,
} from '../../api/autocomplete';
import {
  shouldRedirectToSearch,
  isMainTabsBrowserHistory,
  shouldTriggerSearch,
  getEmail,
} from '../../helpers';
import { useVisualSearch } from '../../hooks/useVisualSearch';
import { definedTranslation } from '../../i18n';
import {
  getIsOpenSelector,
  getQuerySelector,
  getShadowQuerySelector,
  isSuggestionsRequestedSelector,
  currentProductIndexSelector,
  getSuggestedItemsSelector,
  currentItemIndexSelector,
  xRequestIdSelector,
} from '../../redux/selectors/autocompleteSelector';
import {
  setQuery,
  setIsOpen,
  setIsSuggestionsRequested,
  setShadowQuery,
  setCurrentItemIndex,
  setHoveredItem,
  setCurrentProductIndex,
} from '../../redux/sliceCreators/autocompleteSlice';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { ArrowEvents } from '../../types';

const isInspiration = window.location.pathname.includes('/inspiration');

interface SearchFormContainerProps {
  id?: string;
  styledForm?: React.ReactElement<React.FormHTMLAttributes<HTMLFormElement>>;
  onFocus?: (query: string) => void;
  onSubmit?: (query?: string) => void;
  onReset?: () => void;
  submitOnReset?: boolean;
}

const SearchFormContainer: React.FC<SearchFormContainerProps> = ({
  id,
  styledForm,
  onFocus,
  onSubmit,
  onReset,
  submitOnReset = false,
}) => {
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);
  const searchRef = React.useRef<HTMLInputElement | null>(null);
  const query = useAppSelector(getQuerySelector);
  const shadowQuery = useAppSelector(getShadowQuerySelector);
  const isSuggestionsRequested = useAppSelector(isSuggestionsRequestedSelector);
  const currentProductIndex = useAppSelector(currentProductIndexSelector);
  const currentItemIndex = useAppSelector(currentItemIndexSelector);
  const suggestedItems = useAppSelector(getSuggestedItemsSelector);
  const isOpen = useAppSelector(getIsOpenSelector);
  const xRequestId = useAppSelector(xRequestIdSelector);
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { currentProject, loading } = useCurrentProject();
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' });
  const isLoggedIn = Boolean(getEmail());
  const {
    isImageSearchOpened,
    uploadErrors,
    isUploadLoading,
    visualSearchModal,
    handleDragOver,
    handleImageDrop,
    handleImageSearchFormSubmit,
    handleModalClose,
    handleOnDrop,
    handleImageSearchClick,
  } = useVisualSearch();

  const placeholders = {
    project: intl.formatMessage(definedTranslation.placeholders.project, {
      project: currentProject?.title,
    }),
    noProject: intl.formatMessage(definedTranslation.placeholders.noProject),
    inspiration: intl.formatMessage(
      definedTranslation.placeholders.inspiration,
    ),
  };

  const tooltipText = intl.formatMessage(
    definedTranslation.visualSearch.searchTooltip,
  );
  const newLabel = intl.formatMessage(definedTranslation.newLabel);

  function getPlaceholder() {
    if (loading) {
      return '';
    }

    if (isInspiration) {
      return placeholders.inspiration;
    }

    if (currentProject?.title) {
      return placeholders.project;
    }

    return placeholders.noProject;
  }

  useLayoutEffect(() => {
    const isMainTabsHistory = isMainTabsBrowserHistory();
    if (!isMainTabsHistory) {
      sessionStorage.removeItem('persistentQuery');
      sessionStorage.removeItem('persistentColor');
    }
  }, []);

  useEffect(() => {
    window.addEventListener('searchInputSetFocus', handleSearchInputSetFocus);
    window.isSearchFormReady = true;
    window.dispatchEvent(new CustomEvent('search-form-ready'));

    return () => {
      window.removeEventListener(
        'searchInputSetFocus',
        handleSearchInputSetFocus,
      );
      window.isSearchFormReady = false;
    };
  }, []);

  useEffect(() => {
    const handleMiniPdpClose = () => {
      if (currentProductIndex) {
        if (searchRef.current) {
          searchRef.current.focus();
        }
      }
    };

    window.addEventListener('handleMiniPdpCloseEvent', handleMiniPdpClose);
    return () => {
      window.removeEventListener('handleMiniPdpCloseEvent', handleMiniPdpClose);
    };
  }, [shadowQuery, currentProductIndex]);

  const triggerInitSearch = () => {
    if (shouldTriggerSearch(query)) {
      handleSearch({ query });
    }
  };

  useEffect(() => {
    const handleQueryChange = (event: CustomEvent) => {
      const newQuery = event.detail?.query ?? '';
      dispatch(setQuery(newQuery));
      if (newQuery === '') {
        sessionStorage.removeItem('persistentColor');
      }
    };

    triggerInitSearch();
    document.addEventListener('searchQueryResultsChange', handleQueryChange);
    return () => {
      document.removeEventListener(
        'searchQueryResultsChange',
        handleQueryChange,
      );
    };
    // DO NOT ADD the dependencies to the array!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleArrows = (e: React.KeyboardEvent) => {
    switch (e.keyCode) {
      case 38:
        window.dispatchEvent(new CustomEvent(ArrowEvents.Up));
        break;
      case 40:
        window.dispatchEvent(new CustomEvent(ArrowEvents.Down));
        break;
      case 37:
        window.dispatchEvent(new CustomEvent(ArrowEvents.Left));
        break;
      case 39:
        window.dispatchEvent(new CustomEvent(ArrowEvents.Right));
        break;
      default:
        break;
    }
  };

  const handleSearchInputSetFocus = () => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  };

  const handleInputFocus = (currentQuery: string) => {
    dispatch(setIsOpen(true));

    // Keep popup open with same state after miniPDP closed and put focus in Search form
    if (currentProductIndex != null) {
      return;
    }

    if (currentQuery?.length > 0) {
      handleInputChange(currentQuery);
    } else {
      onFocus?.(currentQuery);

      dispatch(initAutocomplete(currentQuery));
    }
  };

  const handleInputChange = (newQuery: string) => {
    dispatch(setShadowQuery(undefined));
    dispatch(setQuery(newQuery));
    dispatch(setCurrentItemIndex(undefined));
    dispatch(setCurrentProductIndex(undefined));
    dispatch(setHoveredItem(undefined));
    if (newQuery?.length < 3 && isSuggestionsRequested) {
      dispatch(setIsSuggestionsRequested(false));
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const newTimer = setTimeout(() => {
      if (newQuery.length > 2) {
        dispatch(fetchSuggestionsByQuery(newQuery));
      } else {
        dispatch(initAutocomplete(newQuery));
      }
    }, 350);
    timerRef.current = newTimer;
  };

  const handleSubmit = (args: string, isReset: boolean = false) => {
    if (currentProductIndex != null && !isReset) {
      const product = suggestedItems?.[currentProductIndex];

      window.dispatchEvent(
        new CustomEvent('initMiniPDP', {
          detail: {
            id: product.variantId || product.id,
            clickedProductRequestId: xRequestId,
          },
        }),
      );
      dispatch(setCurrentProductIndex(undefined));
      return;
    }

    const shadowQ = isReset ? undefined : shadowQuery;
    onSubmit?.(shadowQ || args);
    sessionStorage.setItem('ssScroll', '0');
    sessionStorage.setItem('prevSearchUrl', 'null');
    dispatch(setHoveredItem(undefined));
    dispatch(setIsOpen(false));
    window.scrollTo(0, 0);
    handleSearch({ query: shadowQ || args });
  };

  const handleFormSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    handleSubmit(shadowQuery || query);
  };

  const handleSearch = (args: { query: string }) => {
    if (shouldRedirectToSearch()) {
      const storageLang = getUserLang();
      const searchUrlPrefix = !isEuEnv
        ? ''
        : storageLang != null
        ? `/${storageLang}`
        : '';
      window.location.assign(
        `${searchUrlPrefix}/search?q=${encodeURIComponent(
          args.query || '',
        )}&searchType=Pure search`,
      );
    }

    const searchFormSubmit = new CustomEvent('searchFormSubmit', {
      detail: {
        query: args.query,
        action: searchActionsEnum.searchBar,
        searchType: 'Pure search',
        additionalFilters: {},
      },
    });
    document.dispatchEvent(searchFormSubmit);
    sendSearchAnalyticsEvent({
      query: args.query,
      searchType: 'pure search',
    });
  };

  const handleClose = () => {};

  const handleReset = () => {
    onReset?.();
    handleInputChange('');
    setTimeout(() => {
      handleSearchInputSetFocus();
    }, 200);
    if (submitOnReset) {
      handleSubmit('', true);
    }
  };

  const handleBackButtonClick = () => {
    dispatch(setIsOpen(false));
    window.dispatchEvent(
      new CustomEvent('toggle-main-search', { detail: { show: false } }),
    );
  };

  const handleClick = () => {
    if (!isOpen) {
      dispatch(setIsOpen(true));
    }
  };

  const FormElement = React.isValidElement(styledForm)
    ? () => styledForm
    : styledForm || Styled.Form;
  const withImageSearch = !isMobile && isLoggedIn;

  return (
    <>
      <Styled.Container
        onKeyDown={handleArrows}
        onDrop={handleOnDrop}
        onDragOver={handleDragOver}
      >
        {isOpen && (
          <Styled.BackButton iconName="back" onClick={handleBackButtonClick} />
        )}
        <FormElement
          data-selector="submit-search-form"
          id={id || 'main_search_form'}
          onSubmit={handleFormSubmit}
        >
          <MBSearchFormV2
            isVisualSearchNewFeature={false}
            newTooltipLabel={newLabel}
            imageSearchTooltip={tooltipText}
            ref={searchRef}
            withImageSearch={withImageSearch}
            onInputFocus={() => handleInputFocus(shadowQuery || query)}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
            onClick={handleClick}
            onClose={handleClose}
            onReset={handleReset}
            value={shadowQuery || query}
            placeholder={getPlaceholder()}
            onImageSearchClick={handleImageSearchClick}
            isLeftRightArrowsActive={currentItemIndex == null}
          />
        </FormElement>
      </Styled.Container>
      <UploadImageModal
        showLinkForm
        showCloseIcon={!isUploadLoading}
        isLoading={isUploadLoading}
        showBottomActions={false}
        open={isImageSearchOpened}
        onClose={handleModalClose}
        onSearchFormSubmit={handleImageSearchFormSubmit}
        customErrorMessages={uploadErrors}
        onFilesSelected={handleImageDrop}
        {...visualSearchModal}
      />
    </>
  );
};

export default SearchFormContainer;
